/**
 * Format an integer pence value as £1,234.56.
 * Negative values render as (£1,234.56) in accountant convention.
 *
 * NOTE: for on-screen right-aligned columns use the <Money> component, which
 * pads non-negative values with an invisible ")" so pence digits align with
 * bracketed negatives. (Character padding such as nbsp is NOT reliably the
 * same width as ")" even in monospace stacks — measured 4.8px narrower.)
 */
export function formatGBP(pence: number | string | null | undefined): string {
  if (pence === null || pence === undefined) return '—';
  const p = typeof pence === 'string' ? parseInt(pence, 10) : pence;
  if (isNaN(p)) return '—';
  const abs = Math.abs(p);
  const pounds = abs / 100;
  const formatted = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(pounds);
  return p < 0 ? `(${formatted})` : formatted;
}

/**
 * Format pence as a plain decimal string for input fields (e.g. "-1234.56").
 * Returns empty string for zero so inputs appear blank when unset.
 */
export function penceToPoundString(pence: number | string | null | undefined): string {
  if (pence === null || pence === undefined) return '';
  const p = typeof pence === 'string' ? parseInt(pence, 10) : pence;
  if (isNaN(p) || p === 0) return '';
  return (p / 100).toFixed(2);
}

/**
 * Parse a pound string (e.g. "1234.56" or "-1234.56") to integer pence.
 * Returns 0 if the string is empty or invalid.
 */
export function poundStringToPence(value: string): number {
  if (!value || value.trim() === '') return 0;
  const parsed = parseFloat(value.replace(/[,£]/g, ''));
  if (isNaN(parsed)) return 0;
  return Math.round(parsed * 100);
}

/**
 * Recent period ids for dropdowns, newest first (e.g. ["26-P12", "26-P11", …]).
 * Uses D-005: P1=August, FY-end year is the YY prefix.
 *
 * Starts one month behind the calendar: finance works a month in arrears, so
 * in September the default working period is August. The first element is
 * used as each page's default selection.
 */
export function generateRecentPeriods(count = 12): string[] {
  const now = new Date();
  // First-of-month arithmetic so month-end dates can't skip a month.
  const lagged = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const month = lagged.getMonth() + 1;
  const year = lagged.getFullYear();
  let fy = month >= 8 ? year + 1 : year;
  let p = month >= 8 ? month - 7 : month + 5;
  const periods: string[] = [];
  for (let i = 0; i < count; i++) {
    periods.push(`${String(fy).slice(2)}-P${p}`);
    p -= 1;
    if (p < 1) { p = 12; fy -= 1; }
  }
  return periods;
}

/**
 * Period label for display: "26-P4" → "Nov 25 (P4)"
 * Uses D-005: P1=August, FY-end year is the YY prefix.
 */
export function formatPeriod(period: string): string {
  const match = period.match(/^(\d{2})-P(\d{1,2})$/);
  if (!match) return period;
  const fyEnd = parseInt(match[1], 10) + 2000;
  const p = parseInt(match[2], 10);
  // P1=Aug(8), P2=Sep(9)...P5=Dec(12), P6=Jan(1)...P12=Jul(7)
  const monthOffset = (7 + p - 1) % 12; // 0-based: P1→7(Aug)
  const calMonth = monthOffset + 1; // 1-based
  // Calendar year: months Aug-Dec are fyEnd-1; Jan-Jul are fyEnd
  const calYear = calMonth >= 8 ? fyEnd - 1 : fyEnd;
  const monthName = new Intl.DateTimeFormat('en-GB', { month: 'short' }).format(
    new Date(calYear, calMonth - 1, 1),
  );
  return `${monthName} ${String(calYear).slice(2)} (P${p})`;
}

/**
 * Returns the section display label from the enum value.
 */
export function sectionLabel(section: string): string {
  const map: Record<string, string> = {
    OPERATING: 'Operating Profit & Non-Cash Adjustments',
    WORKING_CAPITAL_DEBTORS: 'Working Capital — Debtors',
    AMOUNTS_DUE_FROM_GROUP: 'Amounts Due from Group',
    WORKING_CAPITAL_CREDITORS: 'Working Capital — Creditors',
    FINANCE_MOVEMENTS: 'Finance Movements',
    INVESTING: 'Investing Activities',
    FINANCING: 'Financing Activities',
  };
  return map[section] ?? section;
}

/**
 * Role display labels.
 */
export function roleLabel(role: string): string {
  const map: Record<string, string> = {
    ADMIN: 'Admin',
    GROUP_FINANCE: 'Group Finance',
    USER: 'User',
  };
  return map[role] ?? role;
}

/**
 * Status display labels for EntityCashFlowStatus.
 */
export function statusLabel(status: string): string {
  const map: Record<string, string> = {
    NOT_STARTED: 'Not Started',
    IN_PROGRESS: 'In Progress',
    SUBMITTED: 'Submitted',
    AGREED: 'Agreed',
  };
  return map[status] ?? status;
}
